import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import Loading from '../../../form/fields/Loading'
import uploadIcon from '../../../assets/icons/Upload.svg'
import './treatment.scss'
import {
  deleteRadiologyDocument,
  displayDocument,
  downloadDocument,
  getMasterEncounterByPatientId,
  getRadiologyDocuments,
  UploadRadiologyDocuments,
} from '../../../services/RadiologyService'
import {
  DELETE_DOCUMENT_ERROR_MESSAGE,
  DELETE_DOCUMENT_SUCCESS_MESSAGE,
} from './Constants'

interface UploadDocsProps {
  patientId: number | string
  muhid: any
  data?: any
}

const IpdUploadDocs: React.FC<UploadDocsProps> = ({
  patientId,
  muhid,
  data,
}) => {
  const docsRef = useRef<HTMLInputElement | null>(null)
  const [uploadedDoc, setUploadedDoc] = useState<any[]>([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedDocUrl, setSelectedDocUrl] = useState('')
  const [loading, setLoading] = useState(false)
  const [masterVisitId, setMasterVisitId] = useState<string | null>(null)

  const fetchPatientDocs = useCallback(
    async (visitId: string) => {
      setLoading(true)
      const params: Record<string, string> = {}
      if (data?.id) {
        params.orderId = String(data?.id)
      }
      if (visitId) {
        params.masterVisitId = visitId
      }
      if (data?.visit_id) {
        params.subVisitId = String(data?.visit_id)
      }
      if (muhid) {
        params.patientId = String(muhid)
      }

      // Convert params object to query string
      const queryString = Object.entries(params)
        .map(([key, value]) => `${key}=${value}`)
        .join('&')
      const response = await getRadiologyDocuments(queryString)

      if (response?.success) {
        setUploadedDoc(response?.data)
      } else {
        setUploadedDoc([])
      }
      setLoading(false)
    },
    [muhid, data]
  )

  const getEncounterId = useCallback(async () => {
    const response = await getMasterEncounterByPatientId(patientId || '')
    if (response?.success && response?.data?.entry) {
      const encounterId = response?.data?.entry?.[0]?.resource?.id
      if (encounterId) {
        setMasterVisitId(encounterId)
        // Fetch documents after getting the encounter ID
        await fetchPatientDocs(encounterId)
      }
    }
  }, [patientId, fetchPatientDocs])

  useEffect(() => {
    if (patientId) {
      getEncounterId()
    }
  }, [patientId, getEncounterId])

  const handleConfirmUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files?.length) return

    if (!masterVisitId) {
      toast.error('Master visit ID not found')
      return
    }

    setLoading(true)
    const formData = new FormData()
    formData.append('file', files[0])
    formData.append('masterVisitId', String(masterVisitId))
    formData.append('patientId', String(muhid))
    if (data?.id) {
      formData.append('orderId', String(data?.id))
    }
    if (data?.visit_id) {
      formData.append('subVisitId', String(data?.visit_id))
    }
    const response = await UploadRadiologyDocuments(formData)
    if (response?.success) {
      toast.success(DELETE_DOCUMENT_SUCCESS_MESSAGE)
      await fetchPatientDocs(masterVisitId)
    } else {
      toast.error(DELETE_DOCUMENT_ERROR_MESSAGE)
    }
    setLoading(false)
  }

  const handleDeleteDocument = async (documentId: string | null) => {
    setLoading(true)
    const response = await deleteRadiologyDocument(
      documentId,
      muhid,
      masterVisitId
    )
    if (response?.success) {
      toast.success(DELETE_DOCUMENT_SUCCESS_MESSAGE)
      if (masterVisitId) {
        await fetchPatientDocs(masterVisitId)
      } else {
        await getEncounterId()
      }
    } else {
      toast.error(response?.message || DELETE_DOCUMENT_ERROR_MESSAGE)
    }
    setLoading(false)
  }

  const DocumentModal = () => {
    const handleCloseModal = () => {
      if (selectedDocUrl && selectedDocUrl.startsWith('blob:')) {
        URL.revokeObjectURL(selectedDocUrl)
      }
      setIsModalOpen(false)
      setSelectedDocUrl('')
    }

    return (
      <div className={`document-modal ${isModalOpen ? 'open' : ''}`}>
        {selectedDocUrl?.endsWith('.pdf') && (
          <div className='document-modal-content'>
            <button className='close-modal-button' onClick={handleCloseModal}>
              Close
            </button>
            <iframe
              src={selectedDocUrl}
              width='100%'
              height='100%'
              title='Document Preview'
            />
          </div>
        )}
      </div>
    )
  }

  const handleIconClick = () => {
    docsRef?.current?.click()
  }

  return (
    <div className='DocContainer'>
      <div className='mainCard'>
        <h3>
          <div className='docViewBody'>
            <div className='docUploadBtn'>
              <div className='doc-upload-container'>
                <label htmlFor='docUrl'>
                  <span className='docUrl'>Patient Documents</span>
                </label>
                <img
                  title='Upload Documents'
                  src={uploadIcon}
                  alt='upload button'
                  className='img-upload-documents'
                  onClick={handleIconClick}
                />
              </div>
              <input
                id='docUrl'
                ref={docsRef}
                type='file'
                accept='image/*, application/pdf'
                className='docUrl-container'
                onChange={handleConfirmUpload}
              />
            </div>
          </div>
        </h3>
        <div className='docView'>
          <ul className='docListView'>
            {uploadedDoc?.length > 0 ? (
              uploadedDoc.map((docUrl: any, index: number) => {
                const serialNumber = index + 1
                return (
                  <li className='docListItem' key={index}>
                    <div className='docItemInfo'>
                      <div className='docItemSerial'>{`${serialNumber} - `}</div>
                      <div className='docItemTitle'>
                        {docUrl?.name || docUrl?.fileName}
                      </div>
                      <div className='docItemButtons'>
                        <button
                          className='docItemButton'
                          onClick={() =>
                            displayDocument(docUrl?.document_id || docUrl?.id)
                          }
                        >
                          Preview
                        </button>
                        <button
                          className='docItemButton'
                          onClick={() =>
                            downloadDocument(docUrl?.document_id || docUrl?.id)
                          }
                        >
                          Download
                        </button>
                        <button
                          className='docItemButton'
                          onClick={() =>
                            handleDeleteDocument(
                              docUrl?.document_id || docUrl?.id
                            )
                          }
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </li>
                )
              })
            ) : (
              <li className='docListItem'>
                <div className='docItemInfo'>No documents found</div>
              </li>
            )}
          </ul>
        </div>
      </div>
      {isModalOpen && <DocumentModal />}
      {loading && <Loading />}
    </div>
  )
}

export default IpdUploadDocs
